import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { CAR_PLATE_REGEX } from '../../const/regex'
import { useSnackbar } from '../Shared/useSnackbar'
import { IUserDataRow } from '../Parking/ParkingPoolUsersTable'
import { IParkingPoolProfile } from '../../graphql/parkingPool/getTenantParkingPoolUsers'
import {
  IQueryUserData,
  IQueryUserVars,
  QUERY_USER,
} from '../../graphql/users/queryUser'
import {
  CHECK_USER_PARKING_POOL_ASSIGNMENT,
  ICheckUserParkingPoolAssignmentVars,
  ICheckUserParkingPoolAssignmentData,
} from '../../graphql/parkingPool/checkUserAssignment'
import { ParkingSubscriptionStatus } from '../../graphql/tenant/getTenant'
import {
  CANCELLED_SUBSCRIPTIONS_LIMIT,
  IQueryUserCancelledReservationsData,
  IQueryUserCancelledReservationsVars,
  QUERY_USER_CANCELLED_RESERVATIONS,
} from '../../graphql/users/queryUserCancelledReservations'
import { cancellationEvents } from '../../utils/cancellationEvents'
import { findDefaultProfile } from '../../utils/defaultParkingPoolProfile'
import {
  SET_PARKING_POOL_USER_DATA,
  ISetParkingPoolUserDataData,
  ISetParkingPoolUserDataVars,
} from '../../graphql/parkingPool/setParkingPoolUserData'
import { IQueryBasicData } from '../../graphql/IQueryData'
import {
  ASSIGN_USER_TO_PARKING_POOL,
  IAssignUserToParkingPoolData,
  IAssignUserToParkingPoolVars,
} from '../../graphql/parkingPool/assignUserToParkingPool'
import {
  UNASSIGN_USER_FROM_PARKING_POOL,
  IUnassignUserFromParkingPoolData,
  IUnassignUserFromParkingPoolVars,
} from '../../graphql/parkingPool/unassignUserFromParkingPool'
import {
  ASSIGN_USER_TO_PARKING_POOL_PROFILE,
  IAssignUserToParkingPoolProfileData,
  IAssignUserToParkingPoolProfileVars,
} from '../../graphql/parkingPool/assignUserToProfile'
import {
  UNASSIGN_USER_FROM_PARKING_POOL_PROFILE,
  IUnassignUserFromParkingPoolProfileData,
  IUnassignUserFromParkingPoolProfileVars,
} from '../../graphql/parkingPool/unassignUserFromProfile'
import {
  REMOVE_ACCESS_CARD_FOR_USER,
  IRemoveAccessCardForUserData,
  IRemoveAccessCardForUserVars,
} from '../../graphql/users/removeAccessCardForUser'
import { NO_PROFILE } from './UserDetailsUi'

const ACTIVE_STATUSES = [
  ParkingSubscriptionStatus.active,
  ParkingSubscriptionStatus.pending,
  ParkingSubscriptionStatus.new,
]

export interface IPlateRow {
  id: string
  value: string
  error?: string
}

interface Params {
  userData: IUserDataRow
  tenantName: string
  parkingPoolId: string
  profiles: IParkingPoolProfile[]
  listRefetch: () => void
}

export const useParkingUserDetails = ({
  userData,
  tenantName,
  parkingPoolId,
  profiles,
  listRefetch,
}: Params) => {
  const { t } = useTranslation(['user_details', 'tenant'])
  const { notify, notifySuccess, notifyError } = useSnackbar()

  const [assigned, setAssigned] = useState(false)
  const [plates, setPlates] = useState<IPlateRow[]>([])
  const plateIdRef = useRef(0)
  const nextPlateId = () => `plate-${(plateIdRef.current += 1)}`
  const [profileId, setProfileId] = useState<string>(NO_PROFILE)
  const [detachOpen, setDetachOpen] = useState(false)
  const [detachWithCard, setDetachWithCard] = useState(false)

  const [checkParkingPoolAssignment] = useLazyQuery<
    ICheckUserParkingPoolAssignmentData,
    ICheckUserParkingPoolAssignmentVars
  >(CHECK_USER_PARKING_POOL_ASSIGNMENT, {
    variables: { tenant: tenantName, login: userData.login },
    fetchPolicy: 'no-cache',
    onCompleted: data =>
      setAssigned(!!data?.getTenant.parkingPool?.users.length),
  })

  const { data, refetch } = useQuery<IQueryUserData, IQueryUserVars>(
    QUERY_USER,
    { variables: { login: userData.login }, fetchPolicy: 'no-cache' },
  )

  const { data: cancelledData, refetch: refetchCancelled } = useQuery<
    IQueryUserCancelledReservationsData,
    IQueryUserCancelledReservationsVars
  >(QUERY_USER_CANCELLED_RESERVATIONS, {
    variables: {
      login: userData.login,
      first: CANCELLED_SUBSCRIPTIONS_LIMIT,
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    checkParkingPoolAssignment()
  }, [])

  const fetchedUser = data?.queryUser[0]
  const lastUser = useRef(fetchedUser)
  if (fetchedUser) lastUser.current = fetchedUser
  const user = fetchedUser ?? lastUser.current

  const parkingPoolData = user?.parkingPoolData

  const savedPlates = [parkingPoolData?.carPlate, parkingPoolData?.carPlate2]
    .filter((plate): plate is string => !!plate)
    .map(plate => plate.toUpperCase())

  const savedPlatesKey = `${user?.login ?? ''}::${savedPlates.join('|')}`
  const lastSavedPlatesKey = useRef<string>()

  useEffect(() => {
    if (!user) return
    if (lastSavedPlatesKey.current === savedPlatesKey) return
    lastSavedPlatesKey.current = savedPlatesKey
    setPlates(savedPlates.map(value => ({ id: nextPlateId(), value })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, savedPlatesKey])

  useEffect(() => {
    if (!user) return
    setProfileId(
      user.parkingPoolProfile?.id ??
        findDefaultProfile(profiles)?.id ??
        profiles[0]?.id ??
        NO_PROFILE,
    )
  }, [user, profiles])

  const refetchAll = () => {
    refetch()
    refetchCancelled()
    checkParkingPoolAssignment()
    listRefetch()
  }

  const [setUserData] = useMutation<
    ISetParkingPoolUserDataData,
    ISetParkingPoolUserDataVars
  >(SET_PARKING_POOL_USER_DATA, { onError: notifyError })

  const succeeded = (payload?: IQueryBasicData): boolean => {
    if (payload?.status) return true
    const reason = payload?.message || payload?.error
    if (reason) notifyError(reason)
    return false
  }

  const writeUserData = async (
    variables: ISetParkingPoolUserDataVars,
  ): Promise<boolean> => {
    const res = await setUserData({ variables })
    return succeeded(res.data?.setParkingPoolUserData)
  }

  const [assignToPool] = useMutation<
    IAssignUserToParkingPoolData,
    IAssignUserToParkingPoolVars
  >(ASSIGN_USER_TO_PARKING_POOL, { onError: notifyError })

  const [unassignFromPool] = useMutation<
    IUnassignUserFromParkingPoolData,
    IUnassignUserFromParkingPoolVars
  >(UNASSIGN_USER_FROM_PARKING_POOL, { onError: notifyError })

  const [assignProfile] = useMutation<
    IAssignUserToParkingPoolProfileData,
    IAssignUserToParkingPoolProfileVars
  >(ASSIGN_USER_TO_PARKING_POOL_PROFILE, { onError: notifyError })

  const [unassignProfile] = useMutation<
    IUnassignUserFromParkingPoolProfileData,
    IUnassignUserFromParkingPoolProfileVars
  >(UNASSIGN_USER_FROM_PARKING_POOL_PROFILE, { onError: notifyError })

  const [removeAccessCard] = useMutation<
    IRemoveAccessCardForUserData,
    IRemoveAccessCardForUserVars
  >(REMOVE_ACCESS_CARD_FOR_USER, { onError: notifyError })

  const hasCard = !!user?.accessCard?.number
  const canEditPlates = hasCard || !!parkingPoolData

  const editedPlates = plates
    .map(({ value }) => value.trim().toUpperCase())
    .filter(Boolean)
  const platesDirty =
    editedPlates.length !== savedPlates.length ||
    editedPlates.some((plate, i) => plate !== savedPlates[i])

  const profileOptions = profiles
  const autoProfileId =
    profileOptions.length === 1 ? profileOptions[0].id : undefined
  const needsProfileChoice = profileOptions.length >= 2
  const profileSatisfied = needsProfileChoice
    ? profileId !== NO_PROFILE
    : autoProfileId !== undefined

  const profileDirty =
    profileId !== (user?.parkingPoolProfile?.id ?? NO_PROFILE)

  const activeReservations = (user?.parkingSubscriptions ?? []).filter(sub =>
    ACTIVE_STATUSES.includes(sub.status),
  )

  const cancellations = cancellationEvents(
    cancelledData?.queryUser[0]?.parkingSubscriptions ?? [],
  )

  const canAssign = hasCard && !!parkingPoolData && profileSatisfied

  const assignHint =
    !hasCard || !parkingPoolData
      ? t('parking_pool:assign_prerequisites')
      : profileOptions.length === 0
        ? t('parking_pool:no_profiles_available')
        : t('parking_pool:select_profile_to_assign')

  const updatePlate = (id: string, value: string) => {
    setPlates(prev =>
      prev.map(row =>
        row.id === id ? { ...row, value, error: undefined } : row,
      ),
    )
  }

  const removePlate = (id: string) => {
    setPlates(prev => prev.filter(row => row.id !== id))
  }

  const addPlate = () =>
    setPlates(prev => [...prev, { id: nextPlateId(), value: '' }])

  const plateError = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    return CAR_PLATE_REGEX.test(trimmed)
      ? undefined
      : t('form:valid_car_plate')
  }

  const savePlates = async (): Promise<boolean> => {
    if (!user) return false
    const validated = plates.map(row => ({
      ...row,
      error: plateError(row.value),
    }))
    setPlates(validated)
    if (validated.some(row => row.error)) return false

    const kept = validated
      .filter(row => row.value.trim())
      .slice(0, 2)
      .map(row => ({ ...row, value: row.value.trim().toUpperCase() }))
    const cleaned = kept.map(row => row.value)

    if (!parkingPoolData && !cleaned.length) {
      setPlates(kept)
      return true
    }

    if (!parkingPoolData && !hasCard) {
      notify(t('parking_pool:no_car_plate_assigned_info'))
      return false
    }

    const written = await writeUserData({
      login: user.login,
      carPlate: cleaned[0] ?? '',
      carPlate2: cleaned[1] ?? '',
      ...(parkingPoolData ? {} : { enabled: true }),
    })
    if (!written) return false

    setPlates(kept)
    return true
  }

  const saveProfile = async () => {
    if (!user) return
    const initialProfileId = user.parkingPoolProfile?.id ?? NO_PROFILE
    if (profileId === initialProfileId) return

    if (profileId === NO_PROFILE) {
      if (user.parkingPoolProfile?.id) {
        await unassignProfile({
          variables: { id: user.parkingPoolProfile.id, login: user.login },
        })
      }
    } else {
      await assignProfile({ variables: { id: profileId, login: user.login } })
    }
  }

  const handleSavePlates = async () => {
    const saved = await savePlates()
    if (!saved) return false
    notifySuccess()
    refetchAll()
    return true
  }

  const handleSaveProfile = async () => {
    await saveProfile()
    notifySuccess()
    refetchAll()
    return true
  }

  const handleAssign = async () => {
    if (!user) return
    const res = await assignToPool({
      variables: { pool: parkingPoolId, login: user.login },
    })
    if (!succeeded(res.data?.assignUserToParkingPool)) return

    const profileToAssign = autoProfileId ?? profileId
    if (profileToAssign && profileToAssign !== NO_PROFILE) {
      await assignProfile({
        variables: { id: profileToAssign, login: user.login },
      })
    }
    const enabled = parkingPoolData
      ? await writeUserData({ login: user.login, enabled: true })
      : true
    if (enabled) notify(t('generic:success_message'))
    refetchAll()
  }

  const handleDetach = async () => {
    if (!user) return
    const res = await unassignFromPool({
      variables: { pool: parkingPoolId, login: user.login },
    })
    setDetachOpen(false)
    if (!succeeded(res.data?.unassignUserFromParkingPool)) return

    const disabled = parkingPoolData
      ? await writeUserData({ login: user.login, enabled: false })
      : true
    if (detachWithCard && hasCard) {
      const res = await removeAccessCard({ variables: { login: user.login } })
      succeeded(res.data?.removeAccessCardForUser)
    }
    if (disabled) notify(t('generic:success_message'))
    refetchAll()
  }

  const requestDetach = () => {
    setDetachWithCard(false)
    setDetachOpen(true)
  }

  return {
    loading: !user,
    user,
    refetch,
    refetchAll,
    notify,
    assigned,
    plates,
    canEditPlates,
    platesDirty,
    updatePlate,
    removePlate,
    addPlate,
    handleSavePlates,
    profileId,
    setProfileId,
    profileOptions,
    profileDirty,
    handleSaveProfile,
    activeReservations,
    cancellations,
    canAssign,
    assignHint,
    hasCard,
    platesSaved: !!parkingPoolData,
    profileSatisfied,
    detachOpen,
    setDetachOpen,
    detachWithCard,
    setDetachWithCard,
    handleAssign,
    requestDetach,
    handleDetach,
  }
}
